import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Divider, Grid, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomButton from "../../../components/common/CustomButton";
import CustomTextField from "../../../components/common/CustomTextField";
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import CustomTextarea from "../../../components/common/CustomTextarea";
import { NEWS_UPDATE, NEWS_VIEW } from '../../../services/apiRoutes';
import CustomStyle from "../../../components/common/CustomStyle";
import CustomAutocomplete from '../../../components/common/CustomAutoComplete';


const leadValidationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    content: Yup.string().required('Content is required'),
    featuredImage: Yup.mixed().required('Featured Image is required'),
    category: Yup.string().required('Category is required'),
    metaTitle: Yup.string().required('Meta Title is required'),
    metaDescription: Yup.string().required('Meta Description is required'),
    views: Yup.number().required('Views is required'),
    publishedAt: Yup.date().required('Published Date is required'),
    status: Yup.string().required('Status is required'),
});

const UpdateNewsPost = ({ handleClose, newsId, setMessage, getNewsList, blogCategory, blogStatus }) => {

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!newsId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');

                const response = await axios.get(NEWS_VIEW(newsId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,

                    },
                });

                setData(response.data.news);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [newsId]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    // ✅ Image change handler
    const handleImageChange = (event, setFieldValue) => {
        const file = event.target.files[0];
        if (file) {
            setFieldValue("featuredImage", file);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const formData = new FormData();

            formData.append("title", values.title);
            formData.append("content", values.content);

            // ✅ Important: only append if file selected
            if (values.featuredImage instanceof File) {
                formData.append("featuredImage", values.featuredImage);
            }

            formData.append("category", values.category);
            formData.append("metaTitle", values.metaTitle);
            formData.append("metaDescription", values.metaDescription);
            formData.append("views", values.views);
            formData.append("publishedAt", values.publishedAt);
            formData.append("status", values.status);

            const token = sessionStorage.getItem('accessToken');

            const response = await axios.put(NEWS_UPDATE(newsId), formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                }
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "News updated successfully",
                    type: "success",
                });

                getNewsList();

                setTimeout(() => {
                    handleClose();
                }, 1000);

            } else {
                alert("Something went wrong");
            }

        } catch (error) {
            alert("Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik
                    initialValues={{
                        title: data?.title || '',
                        content: data?.content || '',
                        featuredImage: data?.featuredImage || '',
                        metaTitle: data?.metaTitle || '',
                        metaDescription: data?.metaDescription || '',
                        views: data?.views || 0,
                        publishedAt: data?.publishedAt || '',
                        category: data?.category ? Number(data.category) : 0,
                        status: data?.status ? Number(data.status) : 0,
                    }}
                    validationSchema={leadValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                        <Form>
                            <Grid container spacing={1} style={CustomStyle.container}>
                                <Grid item xs={6} md={6}>
                                    <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                        Update News
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={6} style={CustomStyle.buttonContainer}>
                                    <Box style={CustomStyle.buttonBox}>
                                        <CustomButton
                                            startIcon={<SaveRounded />}
                                            title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                            size="small"
                                            variant="contained"
                                            type="submit"
                                            disabled={isSubmitting}
                                        />

                                        <CustomButton
                                            startIcon={<ClearRounded />}
                                            title="Cancel"
                                            size="small"
                                            variant="contained"
                                            onClick={handleClose}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider />
                            <Grid container spacing={1} style={{ paddingLeft: "5%", paddingRight: "5%", paddingTop: '5px' }}>

                                <Grid item xs={12} md={6}>
                                    <CustomTextarea
                                        placeholder="Enter title"
                                        name="title"
                                        fieldLabel="Title"
                                        required
                                        value={values.title}
                                        onChange={handleChange}
                                        error={touched.title && !!errors.title}
                                        helperText={touched.title && errors.title}
                                    />
                                </Grid>

                                {/* Category */}
                                <Grid item xs={12} md={6}>

                                    <CustomAutocomplete
                                        options={blogCategory}
                                        fieldLabel="Category"
                                        required={true}
                                        getOptionLabel={(option) => option.title || ""}
                                        value={
                                            blogCategory.find((option) => option.id === values.category) || null
                                        }
                                        onChange={(e, newValue) =>
                                            setFieldValue("category", newValue ? newValue.id : "")
                                        }
                                        placeholder="Select Category"
                                        error={touched.category && !!errors.category}
                                        helperText={touched.category && errors.category}
                                    />
                                </Grid>

                                {/* Content */}
                                <Grid item xs={12}>
                                    <CustomTextarea
                                        placeholder="Enter content"
                                        name="content"
                                        fieldLabel="Content"
                                        required
                                        value={values.content}
                                        onChange={handleChange}
                                        error={touched.content && !!errors.content}
                                        helperText={touched.content && errors.content}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2">Featured Image *</Typography>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageChange(e, setFieldValue)}
                                    />
                                </Grid>


                                {/* Meta Title */}
                                <Grid item xs={12} md={6}>
                                    <CustomTextarea
                                        placeholder="Enter meta title"
                                        name="metaTitle"
                                        fieldLabel="Meta Title"
                                        required
                                        value={values.metaTitle}
                                        onChange={handleChange}
                                        error={touched.metaTitle && !!errors.metaTitle}
                                        helperText={touched.metaTitle && errors.metaTitle}
                                    />
                                </Grid>

                                {/* Meta Description */}
                                <Grid item xs={12}>
                                    <CustomTextarea
                                        placeholder="Enter meta description"
                                        name="metaDescription"
                                        fieldLabel="Meta Description"
                                        required
                                        value={values.metaDescription}
                                        onChange={handleChange}
                                        error={touched.metaDescription && !!errors.metaDescription}
                                        helperText={touched.metaDescription && errors.metaDescription}
                                    />
                                </Grid>

                                {/* Views */}
                                <Grid item xs={12} md={4}>
                                    <CustomTextField
                                        type="number"
                                        placeholder="Enter views"
                                        name="views"
                                        fieldLabel="Views"
                                        required
                                        value={values.views}
                                        onChange={handleChange}
                                        error={touched.views && !!errors.views}
                                        helperText={touched.views && errors.views}
                                    />
                                </Grid>

                                {/* Published Date */}
                                <Grid item xs={12} md={4}>
                                    <CustomTextField
                                        type="date"
                                        name="publishedAt"
                                        fieldLabel="Published Date"
                                        required
                                        value={values.publishedAt}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        error={touched.publishedAt && !!errors.publishedAt}
                                        helperText={touched.publishedAt && errors.publishedAt}
                                    />
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12} md={4}>
                                    <CustomAutocomplete
                                        options={blogStatus}
                                        fieldLabel="Status"
                                        required={true}
                                        getOptionLabel={(option) => option.title}
                                        value={blogStatus.find((option) => option.id === values.status) || null}
                                        onChange={(e, newValue) =>
                                            setFieldValue('status', newValue ? newValue.id : '')
                                        }
                                        placeholder="Select status"
                                        error={touched.status && !!errors.status}
                                        helperText={touched.status && errors.status}
                                    />
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>
        </>
    );
};

export default UpdateNewsPost;

